import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";
import type { Billing } from "@/lib/types";
import { useSession } from "@/lib/session";
import { canManageBilling } from "@/features/org/roles";
import { Head, Stack } from "@/ui/frame/Page";
import { Panel } from "@/ui/Panel";
import { Strip, Reading } from "@/ui/Strip";
import { Lamp } from "@/ui/Lamp";
import { Notice } from "@/ui/Notice";
import { ButtonLink } from "@/ui/Button";
import { ErrorState, Skeleton } from "@/ui/States";
import { useRental } from "@/features/billing/rental";
import { MissingDetailsBanner } from "@/features/billing/panels";
import { PlanChooser } from "@/features/order/PlanChooser";
import { ExtendPanel } from "@/features/order/ExtendPanel";
import { CreditPanel } from "@/features/order/CreditPanel";

export const Route = createFileRoute("/_app/o/$orgId/order")({
  component: OrderScreen,
});

/**
 * Ordering: the three things a customer buys, on one screen.
 *
 * # WHY THIS IS NOT A BILLING TAB
 *
 * Because buying and accounting are different activities with different
 * audiences and different tempos. Billing answers "what happened": a balance,
 * its movements, the documents, the address they are made out to. It is read,
 * occasionally, usually because something needs checking. Ordering answers
 * "what do I want": a plan, a term, credit to pay for them. It is done,
 * deliberately, and it spends money.
 *
 * Mixing them meant the surface that spends money was reached through a tab
 * labelled with the surface that records it, with the actual actions hidden
 * behind two dialogs launched from a page header — so the product had no
 * place where you could see what it sells. It has one now, and Billing went
 * back to being the record.
 *
 * # THE ORDER THE THREE ARE IN
 *
 * What to rent, how long for, and the credit that pays for it. That is the
 * order of the decisions: the plan sets the monthly price, the price times the
 * months is the cost, and the cost is what the credit has to cover.
 *
 * It is not the order a new account does them in — with an empty balance the
 * only possible first step is buying credit. Rather than resequence the page
 * under them, which would move the panel somebody was reading, the shortfall
 * is said once at the top and links down to the panel that fixes it.
 */
function OrderScreen() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const { organisations } = useSession();
  const role = organisations.find((o) => o.id === orgId)?.role;
  const rental = useRental(orgId);

  // Whether this account can be invoiced at all.
  //
  // Not decoration. `TopUps.Quote` refuses outright without a name, an address
  // and a country — the tax depends on where the buyer is, so it is asked
  // rather than guessed — which means the credit panel's very first quote
  // comes back 412 and every figure in it stays empty. The panel does print
  // that message, but the form that fixes it is on billing's documents tab,
  // and a screen that states a precondition without offering the way to meet
  // it is a dead end. This banner is that way.
  const account = useQuery({
    queryKey: ["billing", orgId],
    queryFn: ({ signal }) => api.get<Billing>(`/organisations/${orgId}/billing`, undefined, signal),
    enabled: canManageBilling(role),
  });

  // Ordering is `billing.manage`, which stops at a co-owner. An administrator
  // may read the money and not spend it, so they get the explanation rather
  // than a row of controls that answer 403.
  if (!canManageBilling(role)) {
    return (
      <>
        <Head
          icon={<ShoppingCart size={18} />}
          title="Order"
          description="What this account rents, for how long, and the credit that pays for it."
        />
        <Notice title="Only an owner can order capacity">
          Your role can see what the account rents and what it has spent, on{" "}
          <Link
            to="/o/$orgId/billing"
            params={{ orgId }}
            className="underline decoration-[var(--border-strong)] underline-offset-2 hover:text-[var(--text-primary)]"
          >
            Billing
          </Link>
          . Changing a plan, extending a rental and buying credit are the owner's and
          co-owners'.
        </Notice>
      </>
    );
  }

  if (rental.isLoading) {
    return (
      <>
        <Head icon={<ShoppingCart size={18} />} title="Order" />
        <Skeleton className="h-64 w-full" rounded="lg" />
      </>
    );
  }
  if (rental.isError) {
    return (
      <>
        <Head icon={<ShoppingCart size={18} />} title="Order" />
        <ErrorState error={rental.error} onRetry={() => void rental.refetch()} />
      </>
    );
  }

  const r = rental.data;
  if (!r) return null;

  const ending = r.active && r.days_left <= r.notice_days;
  const short = r.monthly_micros > 0 && r.months_affordable < 1;

  return (
    <>
      <Head
        icon={<ShoppingCart size={18} />}
        title="Order"
        description="Capacity is rented by the month and paid for in advance from credit. Credit is bought with a card and taxed once, on purchase — so renting and upgrading from the balance afterwards produce no further paperwork."
        actions={
          <ButtonLink tone="secondary" to="/o/$orgId/billing" params={{ orgId }}>
            Billing
          </ButtonLink>
        }
      />

      <Strip>
        <Reading label="Balance">{r.balance}</Reading>
        <Reading label="Renting">{r.plan}</Reading>
        {r.monthly_micros > 0 && <Reading label="A month">{r.monthly}</Reading>}
        <Reading
          label={r.active ? "Rental ends in" : "Rental"}
          lamp={
            <Lamp state={!r.paid_through ? "idle" : !r.active ? "down" : ending ? "warn" : "up"} />
          }
        >
          {r.active ? (
            <span className={ending ? "text-[var(--warning)]" : undefined}>
              {r.days_left} {r.days_left === 1 ? "day" : "days"}
            </span>
          ) : r.paid_through ? (
            <span className="text-[var(--danger)]">Ran out</span>
          ) : (
            "Not started"
          )}
        </Reading>
      </Strip>

      <Stack>
        {account.data?.identity_missing && !account.data.exempt && (
          <MissingDetailsBanner
            account={account.data}
            onFix={() =>
              void navigate({
                to: "/o/$orgId/billing/invoices",
                params: { orgId },
                search: { edit: "identity" },
              })
            }
          />
        )}

        {short && (
          <Notice title="The balance does not cover another month">
            Buy credit below, and extending becomes one press.
          </Notice>
        )}

        <Panel
          title="What this account rents"
          description="The plan sets the ceilings and the monthly price. A change is settled against what is left of the rental, and the figure is quoted before anything is spent."
        >
          <PlanChooser orgId={orgId} rental={r} onChanged={() => void rental.refetch()} />
        </Panel>

        <Panel
          title="Rent for longer"
          description="Paid from the balance, added to the end of what is already paid for."
        >
          <ExtendPanel orgId={orgId} rental={r} />
        </Panel>

        <Panel
          title="Buy credit"
          description="Charged to a card. The balance moves when the payment is confirmed, which is usually immediate and occasionally is not."
        >
          <CreditPanel orgId={orgId} rental={r} />
        </Panel>
      </Stack>
    </>
  );
}
